import { ArrowRight, Cpu, FolderGit2, ListChecks, type LucideIcon } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import { agentChapter } from "@/components/guide/chapter-agent";
import { baselineChapter } from "@/components/guide/chapter-baseline";
import { driveChapter } from "@/components/guide/chapter-drive";
import { metricsChapter } from "@/components/guide/chapter-metrics";
import { readingChapter } from "@/components/guide/chapter-reading";
import { setupChapter, type Os } from "@/components/guide/chapter-setup";
import { Hero } from "@/components/guide/hero";
import { Tutorial } from "@/components/guide/tutorial";
import { useApiKey } from "@/hooks/use-auth";

/**
 * The site's front page, in the shape of a tutorial: numbered steps down the left, and a
 * preview beside them that stays put and changes to match the step being read -- a terminal
 * filling up, a file growing a method at a time, a screenshot, a still of the site.
 *
 * Six chapters, in the order the work goes: set up; drive by hand; write an agent against the
 * interface; take the baseline apart -- place recognition, from SIFT to a topological graph --
 * and make it drive; understand the metrics; read further. Then three doors out. The SDK's own
 * documentation is /sdk.
 *
 * The screenshots under public/getting-started/ are real: the viewer and the baseline rendered
 * offscreen from a session on the tutorial's own maze, and its run page on this site.
 */
export function GettingStartedRoute() {
  const apiKey = useApiKey();
  const [os, setOs] = useState<Os>(() =>
    typeof navigator !== "undefined" && /win/i.test(navigator.platform) ? "windows" : "unix",
  );
  const [keyShown, setKeyShown] = useState(false);
  const chapters = [
    setupChapter({ os, setOs, apiKey, keyShown, setKeyShown }),
    driveChapter,
    agentChapter,
    baselineChapter,
    metricsChapter,
    readingChapter,
  ];

  return (
    <div className="guide space-y-12">
      <Hero chapters={chapters.length} minutes={45} first={chapters[0].id} />
      <Tutorial chapters={chapters}>
        <section aria-labelledby="more-title" className="scroll-mt-28">
          <h2 id="more-title" className="text-2xl font-semibold tracking-tight">
            Where next
          </h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <Door icon={ListChecks} to="/challenges" title="Pick a challenge" text="Start with a practice challenge. Each page shows your key and its id." />
            <Door
              icon={Cpu}
              to="/guidelines/local"
              title="On your own machine"
              text="The simulator in your own process: practice on any maze for free, see the truth while you debug, and an experimental RL demo."
            />
            <Door
              icon={FolderGit2}
              href="https://github.com/ai4ce/vis_nav_player"
              title="The starter kit"
              text="The keyboard agent, the skeleton, and a baseline that indexes the exploration frames."
            />
          </div>
          <p className="text-muted-foreground mt-4 text-sm">
            All guides, including the SDK reference:{" "}
            <Link to="/guidelines" className="text-primary underline-offset-4 hover:underline">
              /guidelines
            </Link>
          </p>
        </section>
      </Tutorial>
    </div>
  );
}

function Door({
  icon: Icon,
  title,
  text,
  to,
  href,
}: {
  icon: LucideIcon;
  title: string;
  text: ReactNode;
  to?: string;
  href?: string;
}) {
  const body = (
    <>
      <span className="bg-primary/10 text-primary inline-flex size-8 items-center justify-center rounded-md">
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="mt-3 flex items-center gap-1 font-semibold">
        {title}
        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </span>
      <span className="text-muted-foreground mt-1 block text-sm leading-6">{text}</span>
    </>
  );
  const className = "group bg-card hover:bg-muted/60 block min-w-0 rounded-lg border p-4 transition-colors";
  return to !== undefined ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <a href={href} target="_blank" rel="noreferrer" className={className}>
      {body}
    </a>
  );
}
